import React, { useEffect, useState } from 'react';
import { Box, Typography, Grid, Paper, Skeleton, useTheme, useMediaQuery, Stack } from '@mui/material';
import { NewsArticle, AdPosition } from '../../types';
import NewsCard from './NewsCard';
import AdComponent from '../AdComponent';
import { LoadingSkeleton } from '../common';
import adService from '../../services/adService';

interface FeaturedNewsProps {
  featuredNews: NewsArticle[];
  loading: boolean;
  showAds?: boolean;
}

const FeaturedNews: React.FC<FeaturedNewsProps> = ({ 
  featuredNews, 
  loading, 
  showAds = false
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [topAd, setTopAd] = useState<AdPosition | null>(null);
  const [bottomAd, setBottomAd] = useState<AdPosition | null>(null);
  const [adsLoading, setAdsLoading] = useState(false);
  
  useEffect(() => {
    if (!showAds) return;
    
    const fetchAds = async () => {
      try {
        setAdsLoading(true);
        const screenSize = isMobile ? 'MOBILE' : 'DESKTOP';
        
        // Öne çıkan haberlerin kategori ID'lerini al (ilk haberin kategorisi)
        const categoryId = featuredNews.length > 0 ? featuredNews[0].category?.id : undefined;
        
        // Akıllı reklamları getir - sadece üst reklam kullanarak overload'ı önle
        const aboveAds = await adService.getSmartAds('ABOVE_TITLE', screenSize, undefined, categoryId);
        
        if (aboveAds.length > 0) {
          setTopAd(aboveAds[Math.floor(Math.random() * aboveAds.length)]);
        }
        
        // Alt reklam devre dışı - çok fazla reklam yoğunluğunu engellemek için
        // const belowAds = await adService.getSmartAds('BELOW_TITLE', screenSize, undefined, categoryId);
        // if (belowAds.length > 0) {
        //   setBottomAd(belowAds[Math.floor(Math.random() * belowAds.length)]);
        // }
      } catch (error) {
        console.error('Reklamlar yüklenirken hata oluştu:', error);
      } finally {
        setAdsLoading(false);
      }
    };
    
    fetchAds();
  }, [showAds, isMobile, featuredNews]);

  if (loading) {
    return <LoadingSkeleton variant="featured-news" />;
  }

  if (!featuredNews || featuredNews.length === 0) {
    return null;
  }

  const mainFeature = featuredNews[0];
  
  const secondaryFeatures = featuredNews.slice(1, 3);

  return (
    <Box sx={{ mb: 8 }}>
      {showAds && topAd && (
        <Box sx={{ mb: 4 }}>
                          <AdComponent adCode={topAd.adCode} clickUrl={topAd.clickUrl} />
        </Box>
      )}
      
      <Typography variant="h4" component="h2" gutterBottom sx={{ mb: 4, fontWeight: 700 }}>
        Öne Çıkan Haberler
      </Typography>

      <Stack 
        direction={{ xs: 'column', md: 'row' }} 
        spacing={3}
        sx={{ mb: 5 }}
      >
        <Box sx={{ flex: { md: 3 } }}>
          <NewsCard article={mainFeature} featured />
        </Box>

        {!isMobile && secondaryFeatures.length > 0 && (
          <Stack direction="column" spacing={2} sx={{ flex: 2 }}>
            {secondaryFeatures.map((article) => (
              <Box key={article.id} sx={{ height: '100%' }}>
                <NewsCard article={article} />
              </Box>
            ))}
          </Stack>
        )}
      </Stack>

      {isMobile && secondaryFeatures.length > 0 && (
        <Stack direction="column" spacing={3} sx={{ mt: 3 }}>
          {secondaryFeatures.map((article) => (
            <Box key={article.id}>
              <NewsCard article={article} />
            </Box>
          ))}
        </Stack>
      )}
      
      {showAds && bottomAd && (
        <Box sx={{ mt: 4 }}>
                          <AdComponent adCode={bottomAd.adCode} clickUrl={bottomAd.clickUrl} />
        </Box>
      )}
    </Box>
  );
};

export default FeaturedNews; 